import { useState } from 'react';
import type { GameState } from '@flip7/shared';
import { HS_MASCOTS, HS_MASCOT_IDS } from '@flip7/shared';
import type { MascotId } from '@flip7/shared';

const MASCOT_EMOJI: Record<MascotId, string> = {
  hurley: '🌭',
  gobbler: '🐻',
  dangle: '🐟',
  mum: '👑',
};

const MASCOT_TAGLINE: Record<MascotId, string> = {
  hurley: 'The Bun Banger from Boxford',
  gobbler: 'Glenbrook\'s Ferocious Mascot',
  dangle: 'Ashford\'s Cursed Deep Sea Fish',
  mum: 'Queveland\'s Royal Regent',
};

interface Props {
  gameState: GameState;
  playerId: string | null;
  isHost: boolean;
  onStartGame: () => void;
  onLeave: () => void;
}

export function HotStreakLobbyScreen({ gameState, playerId, isHost, onStartGame, onLeave }: Props) {
  const [copied, setCopied] = useState(false);
  const roomCode = gameState.roomCode;
  const players = gameState.players;

  const copyRoomCode = () => {
    navigator.clipboard.writeText(`F7-${roomCode}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="hs-lobby">
      {/* Back button */}
      <button className="hs-lobby-back" onClick={onLeave} aria-label="Leave">
        &larr;
      </button>

      {/* Title */}
      <div className="hs-lobby-title">
        <span className="hs-lobby-icon">🏇</span>
        <h1>HOT STREAK</h1>
        <p className="hs-lobby-subtitle">Betting, Racing, and SCREAMING!</p>
      </div>

      {/* Room code */}
      <div className="hs-lobby-code">
        <span className="hs-lobby-code-prefix">ROOM</span>
        <button className="hs-lobby-code-value" onClick={copyRoomCode}>
          F7-{roomCode}
          <span className="hs-lobby-code-copy">{copied ? '✓' : '📋'}</span>
        </button>
      </div>

      {/* Mascot showcase */}
      <div className="hs-lobby-mascots">
        {HS_MASCOT_IDS.map((id) => {
          const info = HS_MASCOTS[id];
          return (
            <div key={id} className="hs-lobby-mascot" style={{ '--mascot-color': info.color } as React.CSSProperties}>
              <span className="hs-lobby-mascot-emoji">{MASCOT_EMOJI[id]}</span>
              <span className="hs-lobby-mascot-name">{info.name}</span>
              <span className="hs-lobby-mascot-tagline">{MASCOT_TAGLINE[id]}</span>
            </div>
          );
        })}
      </div>

      {/* How it works */}
      <div className="hs-lobby-rules">
        <h3>How It Works</h3>
        <div className="hs-lobby-steps">
          <div className="hs-lobby-step">
            <span className="hs-lobby-step-num">1</span>
            <span className="hs-lobby-step-icon">🎫</span>
            <span className="hs-lobby-step-text">Bet on mascots</span>
          </div>
          <div className="hs-lobby-step-arrow">→</div>
          <div className="hs-lobby-step">
            <span className="hs-lobby-step-num">2</span>
            <span className="hs-lobby-step-icon">🃏</span>
            <span className="hs-lobby-step-text">Sneak a card into the deck</span>
          </div>
          <div className="hs-lobby-step-arrow">→</div>
          <div className="hs-lobby-step">
            <span className="hs-lobby-step-num">3</span>
            <span className="hs-lobby-step-icon">🏆</span>
            <span className="hs-lobby-step-text">Watch the race & win!</span>
          </div>
        </div>
      </div>

      {/* Player list */}
      <div className="hs-lobby-players">
        <h3>Players ({players.length})</h3>
        <div className="hs-lobby-player-list">
          {players.map((p) => (
            <div key={p.id} className={`hs-lobby-player ${p.id === playerId ? 'hs-lobby-player--you' : ''}`}>
              <span className="hs-lobby-player-name">
                {p.name}
                {p.id === playerId && <span className="hs-lobby-you-badge">You</span>}
              </span>
              {p.isHost && <span className="hs-lobby-host-badge">HOST</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Start button */}
      <div className="hs-lobby-actions">
        {isHost ? (
          <button className="hs-lobby-start" onClick={onStartGame}>
            START RACE
          </button>
        ) : (
          <p className="hs-lobby-waiting">Waiting for host to start...</p>
        )}
      </div>
    </div>
  );
}
